/**
 * DataConfiguration Module
 * 
 * Configuration data for construction worker payslip fields.
 * Defines the structure and mapping of all payslip sections including:
 * - Furikomi (Transfer) items
 * - Payment items
 * - Deduction items
 * - Attendance items
 * - Notes items
 * 
 * Each item has:
 * - id: Unique identifier for the item
 * - name: Display name in Japanese
 * - keyId: Identifier for the key cell in the payslip table
 * - valueId: Identifier for the value cell in the payslip table
 * 
 * @module DataConfiguration
 */

/**
 * Furikomi (Transfer) items configuration.
 * Contains items related to bank transfer amounts.
 * 
 * @constant
 */
export const constructionFurikomiItems = [
  { id: 'construction_furikomi_1', name: '振込金額', keyId: 'cf_k1', valueId: 'cf_v1' } // Transfer amount
];

/**
 * Payment items configuration.
 * Contains items related to salary payments and allowances.
 * 
 * @constant
 */
export const constructionPaymentItems = [
  { id: 'construction_payment_1', name: '基本給', keyId: 'cp_k1', valueId: 'cp_v1' },      // Base salary
  { id: 'construction_payment_2', name: '出来高給', keyId: 'cp_k2', valueId: 'cp_v2' },    // Piece rate pay
  { id: 'construction_payment_3', name: '現場手当', keyId: 'cp_k3', valueId: 'cp_v3' },    // Site allowance
  { id: 'construction_payment_4', name: '時間外手当', keyId: 'cp_k4', valueId: 'cp_v4' },  // Overtime allowance
  { id: 'construction_payment_5', name: '深夜手当', keyId: 'cp_k5', valueId: 'cp_v5' },    // Night shift allowance
  { id: 'construction_payment_6', name: '休日手当', keyId: 'cp_k6', valueId: 'cp_v6' },    // Holiday allowance
  { id: 'construction_payment_7', name: 'その他手当', keyId: 'cp_k7', valueId: 'cp_v7' }    // Other allowance
];

/**
 * Deduction items configuration.
 * Contains items related to insurance, taxes, and other deductions.
 * 
 * @constant
 */
export const constructionDeductionItems = [
  { id: 'construction_deduction_1', name: '健康保険', keyId: 'cd_k1', valueId: 'cd_v1' },       // Health insurance
  { id: 'construction_deduction_2', name: '雇用保険', keyId: 'cd_k2', valueId: 'cd_v2' },       // Employment insurance
  { id: 'construction_deduction_3', name: '厚生年金', keyId: 'cd_k3', valueId: 'cd_v3' },       // Pension insurance
  { id: 'construction_deduction_4', name: '所得税', keyId: 'cd_k4', valueId: 'cd_v4' },         // Income tax
  { id: 'construction_deduction_5', name: '住民税', keyId: 'cd_k5', valueId: 'cd_v5' },         // Resident tax
  { id: 'construction_deduction_6', name: '食費', keyId: 'cd_k6', valueId: 'cd_v6' },           // Meal cost
  { id: 'construction_deduction_7', name: '諸式代', keyId: 'cd_k7', valueId: 'cd_v7' },         // Miscellaneous expenses
  { id: 'construction_deduction_8', name: '布団代', keyId: 'cd_k8', valueId: 'cd_v8' },         // Bedding cost
  { id: 'construction_deduction_9', name: '備品リース代', keyId: 'cd_k9', valueId: 'cd_v9' },   // Equipment lease cost
  { id: 'construction_deduction_10', name: 'ガソリン代', keyId: 'cd_k10', valueId: 'cd_v10' },  // Gasoline cost
  { id: 'construction_deduction_11', name: '仮払金', keyId: 'cd_k11', valueId: 'cd_v11' }       // Advance payment
];

/**
 * その他 (Sonota) section items – shown under その他 with 差引支給額 (display order: その他支給, その他控除, 年末調整還付金).
 *
 * @constant
 */
export const constructionSonotaItems = [
  { id: 'construction_sonota_1', name: 'その他支給', keyId: 'cd_k12', valueId: 'cd_v12' },   // Other payment
  { id: 'construction_sonota_2', name: 'その他控除', keyId: 'cd_k13', valueId: 'cd_v13' },   // Other deduction
  { id: 'construction_sonota_3', name: '年末調整還付金', keyId: 'cd_k14', valueId: 'cd_v14' } // Year-end refund
];

/**
 * Attendance items configuration.
 * Contains items related to attendance tracking.
 * 
 * @constant
 */
export const constructionAttendanceItems = [
  { id: 'construction_attendance_1', name: '出勤日数', keyId: 'ca_k1', valueId: 'ca_v1' }, // Attendance days
  { id: 'construction_attendance_2', name: '有給日数', keyId: 'ca_k2', valueId: 'ca_v2' }, // Paid leave days
  { id: 'construction_attendance_3', name: '有給残日数', keyId: 'ca_k3', valueId: 'ca_v3' }, // Remaining paid leave days
  { id: 'construction_attendance_4', name: '時間外', keyId: 'ca_k4', valueId: 'ca_v4' },    // Overtime hours
  { id: 'construction_attendance_5', name: '深夜', keyId: 'ca_k5', valueId: 'ca_v5' },      // Night shift hours
  { id: 'construction_attendance_6', name: '休日', keyId: 'ca_k6', valueId: 'ca_v6' }     // Holiday hours
];

/**
 * Notes items configuration.
 * その他支給内訳, その他控除内訳, 備考 - shown in memo section when enabled.
 *
 * @constant
 */
export const constructionNotesItems: Array<{ id: string; name: string; keyId: string; valueId: string }> = [
  { id: 'construction_notes_1', name: 'その他支給内訳', keyId: 'otherPaymentBreakdown', valueId: 'cn_v1' },
  { id: 'construction_notes_2', name: 'その他控除内訳', keyId: 'otherDeductionBreakdown', valueId: 'cn_v2' },
  { id: 'construction_notes_3', name: '備考', keyId: 'remarks', valueId: 'cn_v3' }
];

/**
 * 現場賞与 — 振込 section (振込金額 from file).
 */
export const constructionBonusFurikomiItems = [
  { id: 'construction_bonus_furikomi_1', name: '振込金額', keyId: 'cb_f_k1', valueId: 'cb_f_v1' },
];

/** 現場賞与 — 支給 section (賞与). 支給合計 is a fixed last column like salary. */
export const constructionBonusPaymentItems = [
  { id: 'construction_bonus_payment_1', name: '賞与', keyId: 'cb_p_k1', valueId: 'cb_p_v1' },
];

/** 現場賞与 — 控除 section (insurance/tax). 控除合計 is a fixed last column like salary. */
export const constructionBonusDeductionItems = [
  { id: 'construction_bonus_deduction_1', name: '健康保険', keyId: 'cb_d_k1', valueId: 'cb_d_v1' },
  { id: 'construction_bonus_deduction_2', name: '雇用保険', keyId: 'cb_d_k2', valueId: 'cb_d_v2' },
  { id: 'construction_bonus_deduction_3', name: '厚生年金', keyId: 'cb_d_k3', valueId: 'cb_d_v3' },
  { id: 'construction_bonus_deduction_4', name: '所得税', keyId: 'cb_d_k4', valueId: 'cb_d_v4' },
];

/**
 * 現場賞与 — その他 section (その他支給, その他控除).
 * 差引支給額 is rendered as a fixed last column, matching salary.
 */
export const constructionBonusSonotaItems = [
  { id: 'construction_bonus_sonota_1', name: 'その他支給', keyId: 'cb_s_k1', valueId: 'cb_s_v1' },
  { id: 'construction_bonus_sonota_2', name: 'その他控除', keyId: 'cb_s_k2', valueId: 'cb_s_v2' },
];

/** 現場賞与 — memo section (same fields as salary construction memo). */
export const constructionBonusNotesItems: Array<{ id: string; name: string; keyId: string; valueId: string }> = [
  { id: 'construction_bonus_notes_1', name: 'その他支給内訳', keyId: 'otherPaymentBreakdown', valueId: 'cbn_v1' },
  { id: 'construction_bonus_notes_2', name: 'その他控除内訳', keyId: 'otherDeductionBreakdown', valueId: 'cbn_v2' },
  { id: 'construction_bonus_notes_3', name: '備考', keyId: 'remarks', valueId: 'cbn_v3' },
];
